import { fetchBrary } from '../utils/FetchBrary';
import { addNotificationToApp, NotificationType } from './NotificationsActions';
import { fetchPackageInfo } from './PackageActions';
import { fetchCartInfo } from './CartActions';
import { EnrollmentPackageId, PersonId, PlanAvailableToClassId, PlanTypeId, PolicyId } from '../types/api/primaryKeys/PrimaryKey';
import { Nullable } from '../types/custom/Nullable';
import { ReduxDispatch } from '../types/redux/ReduxDispatch';
import { ApiDate } from '../types/custom/ApiDate';
import { Decimal } from '../types/custom/Decimal';
import * as PolicyApi from '../apiCalls/policies/policyApiCalls';
import * as EnrollmentContentApi from '../apiCalls/enrollmentContent/enrollmentContentApiCalls';
import * as PackageApi from '../apiCalls/enrollmentPackages/enrollmentPackageApiCalls';
import { AvailablePlansDetail } from '../types/api/plans/AvailablePlansDetail';
import { PackageMessageContent } from '../types/api/enrollmentContent/PackageMessageContent';
import { Package } from '../types/api/enrollmentPackages/Package';

const ROOT_URL = process.env.REACT_APP_API_URL;

export enum EnrollmentActionType {
    FETCH_WELCOME_PAGE = 'FETCH_WELCOME_PAGE',
    FETCH_WELCOME_PAGE_SUCCESS = 'FETCH_WELCOME_PAGE_SUCCESS',
    FETCH_WELCOME_PAGE_FAILURE = 'FETCH_WELCOME_PAGE_FAILURE',

    FETCH_FINISH_PAGE = 'FETCH_FINISH_PAGE',
    FETCH_FINISH_PAGE_SUCCESS = 'FETCH_FINISH_PAGE_SUCCESS',
    FETCH_FINISH_PAGE_FAILURE = 'FETCH_FINISH_PAGE_FAILURE',

    FETCH_AVAILABLE_PLANS = 'FETCH_AVAILABLE_PLANS',
    FETCH_AVAILABLE_PLANS_SUCCESS = 'FETCH_AVAILABLE_PLANS_SUCCESS',
    FETCH_AVAILABLE_PLANS_FAILURE = 'FETCH_AVAILABLE_PLANS_FAILURE',

    FETCH_INSURABILITY_PAGE = 'FETCH_INSURABILITY_PAGE',
    FETCH_INSURABILITY_PAGE_SUCCESS = 'FETCH_INSURABILITY_PAGE_SUCCESS',
    FETCH_INSURABILITY_PAGE_FAILURE = 'FETCH_INSURABILITY_PAGE_FAILURE'
}

export type EnrollmentAction =
    | ReturnType<typeof requestWelcomePage>
    | ReturnType<typeof requestWelcomePageSuccess>
    | ReturnType<typeof requestWelcomePageFailure>

    | ReturnType<typeof requestFinishPage>
    | ReturnType<typeof requestFinishPageSuccess>
    | ReturnType<typeof requestFinishPageFailure>

    | ReturnType<typeof requestAvailablePlans>
    | ReturnType<typeof requestAvailablePlansSuccess>
    | ReturnType<typeof requestAvailablePlansFailure>

    | ReturnType<typeof requestInsurabilityPage>
    | ReturnType<typeof requestInsurabilityPageSuccess>
    | ReturnType<typeof requestInsurabilityPageFailure>;



export function fetchUpdatePackageProgress(packageId: EnrollmentPackageId, stepNumber: Nullable<number>, refetchPackageAndCartInfo: true): (dispatch: ReduxDispatch) => Promise<Package>;
export function fetchUpdatePackageProgress(packageId: EnrollmentPackageId, stepNumber: Nullable<number>, refetchPackageAndCartInfo: false): (dispatch: ReduxDispatch) => Promise<undefined>;
export function fetchUpdatePackageProgress(packageId: EnrollmentPackageId, stepNumber: Nullable<number>): (dispatch: ReduxDispatch) => Promise<undefined>;
export function fetchUpdatePackageProgress(packageId: EnrollmentPackageId, stepNumber: Nullable<number>, refetchPackageAndCartInfo = false) {
    return async (dispatch: ReduxDispatch) => {
        if(stepNumber) {
            await PackageApi.updatePackageProgress(packageId, stepNumber);
        }

        if(refetchPackageAndCartInfo) {
            const packageInfo = await fetchPackageInfo(packageId)(dispatch);
            await fetchCartInfo(packageId)(dispatch);
            return packageInfo;
        }
    };
 }


/* Welcome page */

function requestWelcomePage() {
    return {
        type: EnrollmentActionType.FETCH_WELCOME_PAGE
    } as const;
}

function requestWelcomePageSuccess(data: PackageMessageContent) {
    return {
        type: EnrollmentActionType.FETCH_WELCOME_PAGE_SUCCESS,
        payload: data
    } as const;
}

function requestWelcomePageFailure(error: unknown) {
    return {
        type: EnrollmentActionType.FETCH_WELCOME_PAGE_FAILURE,
        payload: error
    } as const;
}



export function fetchPackageWelcome(packageId: EnrollmentPackageId) {
    return async (dispatch: ReduxDispatch) => {
        dispatch(requestWelcomePage());

        try {       
            const json = await EnrollmentContentApi.getWelcomeContentForPackage(packageId);
            dispatch(requestWelcomePageSuccess(json));
        }
        catch (err) {
            dispatch(requestWelcomePageFailure(err));
        }
    };
}

/* Finish Page */

function requestFinishPage() {
    return {
        type: EnrollmentActionType.FETCH_FINISH_PAGE
    } as const;
}

function requestFinishPageSuccess(data: PackageMessageContent) {
    return {
        type: EnrollmentActionType.FETCH_FINISH_PAGE_SUCCESS,
        payload: data
    } as const;
}

function requestFinishPageFailure(error: unknown) {
    return {
        type: EnrollmentActionType.FETCH_FINISH_PAGE_FAILURE,
        payload: error
    } as const;
}

export function fetchPackageFinish(packageId: EnrollmentPackageId) {
    return async (dispatch: ReduxDispatch) => {
        dispatch(requestFinishPage());

        try {
            const json = await EnrollmentContentApi.getFinishContentForPackage(packageId);
            dispatch(requestFinishPageSuccess(json));
        }
        catch (err) {
            dispatch(requestFinishPageFailure(err));
        }
    };
}

/* Plan selection page */

function requestAvailablePlans() {
    return {
        type: EnrollmentActionType.FETCH_AVAILABLE_PLANS
    } as const;
}

function requestAvailablePlansSuccess(plans: AvailablePlansDetail) {
    return {
        type: EnrollmentActionType.FETCH_AVAILABLE_PLANS_SUCCESS,
        payload: plans
    } as const;
}

function requestAvailablePlansFailure(error: unknown) {
    return {
        type: EnrollmentActionType.FETCH_AVAILABLE_PLANS_FAILURE,
        payload: error
    } as const;
}

export function fetchAvailablePlans(packageId: EnrollmentPackageId, planTypeId: PlanTypeId) {
    return async (dispatch: ReduxDispatch) => {
        dispatch(requestAvailablePlans());

        try {
            const json = await PolicyApi.getAvailablePlansForPlanType(packageId, planTypeId);
            dispatch(requestAvailablePlansSuccess(json));
        }
        catch (err) {
            dispatch(requestAvailablePlansFailure(err));
        }
    };
}


/* Add a Policy */

export function fetchAddPolicy(
    packageId: EnrollmentPackageId, 
    classPlanId: PlanAvailableToClassId, 
    startDate: ApiDate, 
    participantIdList: readonly PersonId[], 
    selectedVolume: Decimal, 
    useDefinedContribution: boolean) {

    return async (dispatch: ReduxDispatch) => {

        const body = {
            planAvailableToClassId: classPlanId,
            startDate: startDate,

            // Warning - the "toString" here is very much intentional for now - the back end is using JOBject and WILL NOT
            // automatically handle a proper array. a JS array.ToString returns like so: "3,5,1", without the square brackets.
            participantPersonIds: participantIdList.toString(),

            selectedVolume: selectedVolume,
            useDefinedContribution: useDefinedContribution
        };

        try {
            await fetchBrary.post(`${ROOT_URL}/api/packages/${packageId}/addpolicy`, body, false);
        }
        catch {
            dispatch(addNotificationToApp("Failed to add policy.", NotificationType.Error));
        }
    };
}

/* Modify a Policy */


export function fetchModifyPolicy(
    packageId: EnrollmentPackageId, 
    policyId: PolicyId, 
    startDate: ApiDate, 
    participantIdList: readonly PersonId[], 
    selectedVolume: Decimal) {
    return async (dispatch: ReduxDispatch) => {

        const body = {
            // Warning - the "toString" here is very much intentional for now - the back end is using JOBject and WILL NOT
            // automatically handle a proper array. a JS array.ToString returns like so: "3,5,1", without the square brackets.
            participantPersonIds: participantIdList.toString(),

            selectedVolume: selectedVolume,
            startDate: startDate
        };

        try {
            await fetchBrary.post(`${ROOT_URL}/api/packages/${packageId}/modifypolicy/${policyId}`, body, false);
        }
        catch {
            dispatch(addNotificationToApp("Failed to modify policy.", NotificationType.Error));
        }
    };
}


/* Insurability page */

export function fetchUpdateInsurability(packageId: EnrollmentPackageId, insurability: unknown) {
    return async (dispatch: ReduxDispatch) => {

        try {
            const json = await fetchBrary.post(`${ROOT_URL}/api/packages/${packageId}/insurability`, insurability);
            return true;
        }
        catch (err) {
            return false;
        }
    };
}


/* Remove a Policy */

export function fetchRemovePolicy(packageId: EnrollmentPackageId, policyId: PolicyId) {
    return async (dispatch: ReduxDispatch) => {
        const body = {};

        try {
            await fetchBrary.post(`${ROOT_URL}/api/packages/${packageId}/removepolicy/${policyId}`, body, false);
            fetchCartInfo(packageId)(dispatch);

            dispatch(addNotificationToApp("Successfully Removed Policy", NotificationType.Success));
        }
        catch {
            dispatch(addNotificationToApp("The selected policy can not be removed.", NotificationType.Error));
        }
    };
}

/* undo remove */
export function fetchUndoRemovePolicy(packageId: EnrollmentPackageId, policyId: PolicyId) {
    return async (dispatch: ReduxDispatch) => {

        const body = {};

        try {
            await fetchBrary.post(`${ROOT_URL}/api/packages/${packageId}/undoremovepolicy/${policyId}`, body, false);
            fetchCartInfo(packageId)(dispatch);

            dispatch(addNotificationToApp("Successfully Un-Removed Policy", NotificationType.Success));
        }
        catch {
            dispatch(addNotificationToApp("The selected policy can not be un-removed.", NotificationType.Error));
        }
    };
}

/* Insurability page */

function requestInsurabilityPage() {
    return {
        type: EnrollmentActionType.FETCH_INSURABILITY_PAGE
    } as const;
}

function requestInsurabilityPageSuccess(insurability: unknown) {
    return {
        type: EnrollmentActionType.FETCH_INSURABILITY_PAGE_SUCCESS,
        payload: insurability
    } as const;
}

function requestInsurabilityPageFailure(error: unknown) {
    return {
        type: EnrollmentActionType.FETCH_INSURABILITY_PAGE_FAILURE,
        payload: error
    } as const;
}

export function fetchInsurability(packageId: EnrollmentPackageId) {
    return async (dispatch: ReduxDispatch) => {
        dispatch(requestInsurabilityPage());

        try {
            const json = await fetchBrary.get(`${ROOT_URL}/api/packages/${packageId}/insurability`);
            dispatch(requestInsurabilityPageSuccess(json));
        }
        catch (err) {
            dispatch(requestInsurabilityPageFailure(err));
        }
    };
}
